import { useAtom, useSetAtom } from 'jotai';
import TagManager from 'react-gtm-module';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  chunkLinksAtom,
  getStepAtom,
  sendCartToApAtom,
  showAmazonManualLoadModalAtom,
  showChunkCartModalAtom,
  showFeedbackModalAtom,
  showInstacartRedirectModalAtom,
} from 'src/store';
import type { CartGetData } from 'src/types';
import { isBannedRetailer, isDisabledRetailer, sanitizeSourceParam } from 'src/utils';
import * as sendCart from 'src/utils/get/sendCart';
import { useExtension, useGetFeedback } from '.';

type ContinueWithItemsArgs = {
  id: string;
  cart: CartGetData;
  handleSplitNewVendor?: boolean;
  handleSplitOriginalVendor?: boolean;
  ignoreSavings?: boolean;
  isChunk?: boolean;
  doneWithChunks?: boolean;
  chunkUrl?: string;
  page: 'get' | 'embed' | 'receive';
  // Forces Amazon chunking without requiring the enable_chunk URL param. Has no effect on non-Amazon vendors.
  forceChunk?: boolean;
};

export const useContinueWithItems = () => {
  const [step, setStep] = useAtom(getStepAtom);
  const [, setChunkLinks] = useAtom(chunkLinksAtom);
  const [, setShowChunkCartModal] = useAtom(showChunkCartModalAtom);
  const [sendCartToAp] = useAtom(sendCartToApAtom);
  const [, setShowFeedbackModal] = useAtom(showFeedbackModalAtom);
  const setShowInstacartRedirectModal = useSetAtom(showInstacartRedirectModalAtom);
  const setShowAmazonManualLoadModal = useSetAtom(showAmazonManualLoadModalAtom);

  const [searchParams, setSearchParams] = useSearchParams();
  const { sendCartToExtension } = useExtension();
  const { shouldReviewBeShown } = useGetFeedback();
  const nav = useNavigate();
  const isChunkAmazonEnabled = sendCart.AMAZON_CHUNK_VARIANT === 'modal-only';

  const handleContinue = async ({
    id,
    cart,
    handleSplitNewVendor,
    handleSplitOriginalVendor,
    ignoreSavings = false,
    isChunk = false,
    doneWithChunks = false,
    chunkUrl,
    page,
    forceChunk,
  }: ContinueWithItemsArgs) => {
    // straight to jail.
    if (isBannedRetailer(cart) || isDisabledRetailer(cart)) {
      return;
    }

    // if we are on the embed page, we should not show the compare cart view
    if (page === 'get' && step === 2 && cart.showSplit) {
      setSearchParams({ split: 'true' });
      setStep(3);
    } else {
      return pushCartFinal({
        id,
        cart,
        handleSplitNewVendor,
        handleSplitOriginalVendor,
        ignoreSavings,
        isChunk,
        doneWithChunks,
        chunkUrl,
        page,
        forceChunk,
      });
    }
  };

  // shouldReviewBeShown will return true based on certain conditions pertaining to the user seeing, interacting with, and completing the review form
  const showFeedbackIfApplicable = () => {
    if (shouldReviewBeShown()) {
      setShowFeedbackModal(true);
    }
  };

  const openItemsAndFeedback = (
    cartUrl: string | undefined,
    cartVendor: string,
    isChunk: boolean,
    doneWithChunks?: boolean, // this will be false if we are loading the cart in chunks and it's not the last chunk
  ) => {
    // if we are chunking the cart and it's the 'all done' button, we won't pass a URL but we still want to check to show feedback
    if (cartUrl) {
      window.open(`${cartUrl}`);
    }

    // we don't want to show the feedback if we are chunking the cart and it's not done yet
    if (isChunk && !doneWithChunks) {
      return;
    }

    showFeedbackIfApplicable();
  };

  const pushCartFinal = ({
    id,
    cart,
    handleSplitNewVendor,
    handleSplitOriginalVendor,
    ignoreSavings,
    isChunk,
    doneWithChunks,
    chunkUrl,
    page,
    forceChunk,
  }: ContinueWithItemsArgs): false | void | 'receive_chunk' => {
    const pagePretty = page === 'get' ? 'Get' : page === 'embed' ? 'Embed' : 'Receive';

    // A ?source= means the cart load started elsewhere (extension or mobile app) and was handed to the site.
    const sourceParam = sanitizeSourceParam(searchParams.get('source'));

    const sourceSearchParam = `source=${sourceParam}`; // used for redirects to the receive page

    let cartUrl: string | undefined = undefined;
    let cartVendor = cart.vendor;
    let cartStore = cart.store;
    let cartItems = cart.items;
    let loggerUrl = `/uv/${id}/`;

    // if we are splitting (new vendor or original with less items)
    // only get the items for split original vendor or split new vendor
    if (handleSplitNewVendor) {
      cartVendor = cart.matchTo as string;
      cartStore = `https://www.${cartVendor}.com`; // TODO - this probably needs to change. Is it even used???
      cartItems = cart.matchedVendorCart.items;

      loggerUrl = `/uvv/${id}/${cartVendor}/`;

      TagManager.dataLayer({
        dataLayer: {
          event: 'Savings',
          eventCategory: 'engagement',
          eventLabel: 'Savings Taken',
          eventValue: {
            savingsValue: Number(cart.matchedVendorCart?.splitItemsTotalPrice) || 0,
            cartCCY: cart.cartCCY,
            cartId: id,
            originalVendor: cart.vendorDisplayName,
            savingsVendor: cart.matchedVendorCart.matchToDisplayName,
          },
        },
      });
    } else if (handleSplitOriginalVendor) {
      cartItems = cart.originalVendorCart.items;
      TagManager.dataLayer({
        dataLayer: {
          event: 'Savings',
          eventCategory: 'engagement',
          eventLabel: 'Non Savings Partial',
          eventValue: {
            savingsValue: Number(cart.matchedVendorCart?.splitItemsTotalPrice) || 0,
            cartCCY: cart.cartCCY,
            cartId: id,
            originalVendor: cart.vendorDisplayName,
            savingsVendor: cart.matchedVendorCart.matchToDisplayName,
          },
        },
      });
    } else if (ignoreSavings) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'Savings',
          eventCategory: 'engagement',
          eventLabel: 'Savings Ignored',
          eventValue: {
            savingsValue: Number(cart.matchedVendorCart?.splitItemsTotalPrice) || 0,
            cartCCY: cart.cartCCY,
            cartId: id,
            originalVendor: cart.vendorDisplayName,
            savingsVendor: cart.matchedVendorCart.matchToDisplayName,
          },
        },
      });
    } else if (cartVendor !== 'walmart') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'Send to Vendor',
          eventCategory: 'engagement',
          eventLabel: `${pagePretty} Page`,
          eventValue: {
            cartValue: Number(cart.cartTotalPrice),
            cartCCY: cart.cartCCY,
            cartId: id,
            cartVendor: cart.vendorDisplayName,
          },
        },
      });
    }

    if (cartVendor === 'amazon') {
      if (sendCart.isAmazonLocalCart(cartStore)) {
        sendCart.sendCartToAmazonLocal({
          cartStore,
          cartItems,
          cartReferrer: cart.referrer || undefined,
          tagParam: searchParams.get('tag'),
        });
        // sendCartToExtension({ cartId: id, vendor: cartVendor });
        return;
      }

      // Marketplaces without aws/cart/add (e.g. amazon.ie) can only be loaded manually.
      // On the receive page, returning false lets the RedirectionTakingTooLongModal continue to its manual flow.
      if (sendCart.isAmazonManualLoadStore(cartStore)) {
        if (page === 'receive') {
          return false;
        }

        setShowAmazonManualLoadModal(true);
        return;
      }

      if (isChunk) {
        openItemsAndFeedback(chunkUrl, cartVendor, true, doneWithChunks);

        return;
      }

      const tag = searchParams.get('tag');

      const amazonStatus = sendCart.sendCartToAmazon({
        cartStore,
        cartItems,
        cartReferrer: cart.referrer || undefined,
        tagParam: tag,
        loggerUrl,
        chunkEnabled: forceChunk || searchParams.has('enable_chunk') || !!isChunkAmazonEnabled,
        sourceParam,
      });

      if (amazonStatus.cartChunks && amazonStatus.cartChunks.length > 0) {
        setChunkLinks(amazonStatus.cartChunks);
        setShowChunkCartModal(true);

        // cart needs chunking but we are on the receive page, so we need to redirect to the get page and open the modal
        if (page === 'receive') {
          nav(`/get/${id}?show_chunk=true&${sourceSearchParam}`);
        }

        return; // feedback will be shown after the user finishes all chunks
      } else {
        cartUrl = amazonStatus.cartUrl;
      }
    } else if (
      cartVendor === 'walmart' &&
      (cartStore === 'https://www.walmart.com' ||
        (cart.dest && cart.dest === 'business.walmart.com'))
    ) {
      if (isChunk) {
        openItemsAndFeedback(chunkUrl, cartVendor, true, doneWithChunks);

        return;
      }

      const walmartStatus = sendCart.sendCartToWalmart({
        cartId: id,
        cartStore,
        cartItems,
        cartAp: cart.ap,
        cartDest: cart.dest || undefined,
        loggerUrl,
        cartValue: Number(cart.cartTotalPrice) || 0,
        cartCCY: cart.cartCCY,
        page: pagePretty,
        sendCartToAp,
        sourceParam,
      });

      if (walmartStatus.cartChunks && walmartStatus.cartChunks.length > 0) {
        setChunkLinks(walmartStatus.cartChunks);
        setShowChunkCartModal(true);

        // cart needs chunking but we are on the receive page, so we need to redirect to the get page and open the modal
        if (page === 'receive') {
          nav(`/get/${id}?show_chunk=true&${sourceSearchParam}`);
        }

        return 'receive_chunk'; // return here b/c we don't want to send the cart yet, we will show the chunk modal on the get page
      }

      cartUrl = walmartStatus.cartUrl;
    } else if (cartVendor === 'bestbuy' && cartStore === 'https://www.bestbuy.com') {
      cartUrl = `${loggerUrl}${sendCart.sendCartToBestBuy({ cartItems })}/${sourceParam}`;
    } else if (cartVendor === 'bhphotovideo') {
      cartUrl = `${loggerUrl}${sendCart.sendCartToBHPhotoVideo({ cartItems })}/${sourceParam}`;
    } else if (cartVendor === 'ikea') {
      cartUrl = `${loggerUrl}${sendCart.sendCartToIkea({ cartItems, cartAp: cart.ap })}/${sourceParam}`;
    } else if (cartVendor === 'instacart' && searchParams.has('instacart-demo')) {
      return sendCart.sendCartToInstacart({
        cartId: id,
        page,
        showFeedbackIfApplicable,
        setShowLoading: setShowInstacartRedirectModal,
      });
    } else if (cartVendor === 'kroger') {
      // KROGER does not allow this to work from an iframe, i.e. the embed view
      return sendCart.sendCartToKroger({ cartId: id, page });
    } else if (cartVendor === 'lowes') {
      cartUrl = `${loggerUrl}${sendCart.sendCartToLowes({ cartId: id, cartItems })}/${sourceParam}`;
    } else if (cartVendor === 'shopify') {
      // ap will NOT be undefined for Shopify
      return sendCart.sendCartToShopify({
        cartId: id,
        cartItems,
        cartAp: cart.ap!,
        sendCartToExtension,
        loggerUrl,
        sourceParam,
        showFeedbackIfApplicable,
      });
    } else if (cartVendor === 'mscdirect') {
      cartUrl = `${loggerUrl}${sendCart.sendCartToMscdirect({ cartId: id, cartItems })}/${sourceParam}`;
    } else if (cartVendor === 'newegg') {
      cartUrl = `${loggerUrl}${sendCart.sendCartToNewegg({ cartItems, cartStore })}/${sourceParam}`;
    } else if (cartVendor) {
      // we don't want to run this on the receive page
      if (page === 'receive') {
        return false;
      }

      return sendCart.sendCartToOtherVendors({
        cartId: id,
        sendCartToExtension,
        cartVendor,
        sourceParam,
        // showFeedbackIfApplicable,
      });
    }

    openItemsAndFeedback(cartUrl, cartVendor, false);

    return;
  };

  return handleContinue;
};
